import { ApiClientError } from "./errors";

export type ClientResult<T, E = string> = [T, null] | [null, E];

export enum HttpMethod {
	Get = "GET",
	Post = "POST",
	Put = "PUT",
	Patch = "PATCH",
	Delete = "DELETE",
	Head = "HEAD",
	Options = "OPTIONS",
}

export interface Repository {
	name: string;
	insert: <TInput, TOutput = void>(input: TInput) => Promise<TOutput>;
	delete: <TOutput = void>(id: string) => Promise<TOutput>;
	update: <TData>(id: string, updates: Partial<TData>) => Promise<TData>;
	find: <TData = void>(id: string) => Promise<TData>;
	findAll: <TOutput>() => Promise<TOutput[]>;
}

export type ExtractRouteParams<T extends string> =
	T extends `${string}:${infer Param}/${infer Rest}`
		? { [K in Param | keyof ExtractRouteParams<Rest>]: string }
		: T extends `${string}:${infer Param}`
			? { [K in Param]: string }
			: Record<string, ParamValue>;

export type ParamValue =
	| string
	| boolean
	| number
	| null
	| Array<ParamValue>
	| { [key: string]: ParamValue };
export type SearchParams = Record<string, ParamValue>;
export type Params = Record<string, ParamValue>;
export type Data =
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- FIX: (bwilytsch) remove any later
	Record<string, any> | File | FormData | string | number | boolean | undefined;

interface TransferProgress {
	loaded: number;
	total: number;
	percentage: number;
}

interface ProgressOptions {
	onProgress?: (progress: TransferProgress) => void;
	onComplete?: () => void;
	onError?: (error: Error) => void;
}

export interface UploadOptions extends ProgressOptions {}
export interface DownloadOptions extends ProgressOptions {}

// Add advanced types that infer the dyanmic parameters from the url argument
//
export interface Options<TParams extends Params> {
	params?: TParams;
	data?: Data;
	query?: SearchParams;
	signal?: AbortSignal;
}

export interface GetOptions<TParams extends Params> {
	params?: TParams;
	query?: SearchParams;
}

export interface Middleware {
	onRequest?: (request: Request) => Promise<Request>;
	onError?: (error: ApiClientError) => Promise<void>;
}

export interface RequestMiddleware extends Middleware {
	onRequest: (request: Request | XMLHttpRequest) => Promise<Request>;
}

export function isRequestMiddleware(m: Middleware): m is RequestMiddleware {
	return typeof m.onRequest === "function";
}

export interface ErrorMiddleware extends Middleware {
	onError: (error: ApiClientError) => Promise<void>;
}

export function isErrorMiddleware(m: Middleware): m is ErrorMiddleware {
	return typeof m.onError === "function";
}

export enum ContentType {
	MultiFile = "multipart/form-data",
	Form = "application/x-www-form-urlencoded",
	Json = "application/json",
	Html = "text/html",
}

export interface MiddlewareConsumer {
	use: (middleware: Middleware | Set<Middleware>) => void;
}
